/**
 * A routine whose warehouse account was removed (ACT2-1286 PR 2b, spec UI 14). A workflow is repaired
 * per query step, so the banner offers one **Repair** per broken step; the dialog probes and writes
 * only that step. A run that found the gone account paused the routine, and only an admin's Resume
 * (which probes every step) takes it out of that pause. With the repair flag off this renders nothing.
 */
import type { WorkflowAccountRepairView } from '@server/warehouse-repair-state';
import type { QueryKey } from '@tanstack/react-query';
import { Play } from 'lucide-react';

import { removedHeadline, unassignedHeadline } from '@/_shared/components/AccountRepair/copy';
import { RelinkNotice } from '@/_shared/components/AccountRepair/RelinkNotice';
import { RepairButton } from '@/_shared/components/AccountRepair/RepairButton';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';

import { isBrokenStep, queryStepLabel } from '../lib/repair';

export function WorkflowRepairBanner({
  workflowId,
  view,
  invalidateKeys,
  isAdmin,
  onResume,
  resuming,
}: {
  workflowId: string;
  view: WorkflowAccountRepairView;
  invalidateKeys: readonly QueryKey[];
  isAdmin: boolean;
  onResume: () => void;
  resuming: boolean;
}): React.ReactElement | null {
  if (!view.repairEnabled) return null;
  const broken = view.nodes.filter(isBrokenStep);
  const notices = view.nodes.filter((n) => n.state === 'ok' && n.notice);
  if (broken.length === 0 && !view.repairPaused && notices.length === 0) return null;

  const allUnassigned = broken.length > 0 && broken.every((n) => n.state === 'unassigned');
  return (
    <div className="flex flex-col gap-2 border-b border-border px-6 py-3">
      {broken.length > 0 ? (
        <Callout variant="warning" role="status">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                {allUnassigned ? unassignedHeadline('workflow') : removedHeadline('workflow')}
              </p>
              <p>
                {view.repairPaused
                  ? `Its query can't run, so it is paused.${view.canRepair ? ' Repair it to pick another account.' : ''}`
                  : "Its query can't run until it is repaired."}
              </p>
              {!view.canRepair && <p>Ask a workspace admin to repair it.</p>}
            </div>
            {view.canRepair && (
              <div className="flex flex-wrap gap-2">
                {broken.map((node) => {
                  const label = queryStepLabel(view.nodes, node.nodeId);
                  return (
                    <RepairButton
                      key={node.nodeId}
                      itemRef={{ itemType: 'workflow', id: workflowId, nodeId: node.nodeId }}
                      itemNoun="workflow"
                      unassigned={node.state === 'unassigned'}
                      invalidateKeys={invalidateKeys}
                      subject={view.nodes.length > 1 ? label : undefined}
                      label={broken.length > 1 || view.nodes.length > 1 ? `Repair ${label}` : 'Repair'}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </Callout>
      ) : (
        view.repairPaused && (
          <Callout variant="info" role="status">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium">Repaired. Resume to run again.</p>
                <p>
                  {isAdmin
                    ? 'Resuming checks every query step on its account first.'
                    : 'Ask a workspace admin to resume it.'}
                </p>
              </div>
              {/* Spec UI 14–15: a member never sees Resume. */}
              {isAdmin && (
                <Button
                  type="button"
                  size="sm"
                  Icon={<Play aria-hidden />}
                  label={resuming ? 'Resuming…' : 'Resume'}
                  loading={resuming}
                  onClick={onResume}
                />
              )}
            </div>
          </Callout>
        )
      )}
      {notices.map((node) => (
        <RelinkNotice
          key={node.nodeId}
          notice={node.notice}
          itemRef={{ itemType: 'workflow', id: workflowId, nodeId: node.nodeId }}
          invalidateKeys={invalidateKeys}
          repairEnabled={view.repairEnabled}
          prefix={view.nodes.length > 1 ? queryStepLabel(view.nodes, node.nodeId) : undefined}
        />
      ))}
    </div>
  );
}
